/**
 * signHeader: WEHAGO API 요청 헤더 서명 유틸리티
 * 원본: wehago-conference/src/utils/signHeader.js
 *
 * HMAC-SHA256 기반 요청 헤더 난독화.
 * http://wiki.duzon.com:8080/pages/viewpage.action?pageId=196027786
 */
import CryptoJS from 'crypto-js';
import { v1 as uuidV1 } from 'uuid';
import { getCookie } from '@/utils/cookie';

// --- 심볼 기반 문자열 난독화 ---
const n = Symbol('n');
const a = Symbol('a');
const g = Symbol('g');
const o = Symbol('o');
const s = Symbol('s');
const w = Symbol('w');
const e = Symbol('e');
const h = Symbol('h');
const i = Symbol('i');

const ws1 = [w.description, e.description, h.description, a.description, g.description, o.description];
const ws2 = [s.description, i.description, g.description, n.description];

export const ws3 = [ws1.join(''), ws2.join('')].join('-');
export const ws4 = [ws1.join(''), s.description].join('_');

const getStorageKeyData = (key: string): string | null => {
  if (getCookie(key)) return getCookie(key);
  if (localStorage.getItem(key)) return localStorage.getItem(key);
  if (sessionStorage.getItem(key)) return sessionStorage.getItem(key);
  return null;
};

// --- URL 파싱 ---
interface ParsedLocation {
  protocol: string;
  host: string;
  hostname: string;
  port: string | undefined;
  pathname: string;
  search: string;
  hash: string;
}

const getLocation = (url: string): ParsedLocation | null => {
  // eslint-disable-next-line no-useless-escape
  const match = url.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
  if (!match) return null;
  return {
    protocol: match[1],
    host: match[2],
    hostname: match[3],
    port: match[4],
    pathname: match[5],
    search: match[6],
    hash: match[7],
  };
};

// --- 서명 생성 ---
const getTransactionId = (): string => uuidV1().replace(/-/g, '');

const getWS = (
  url: ParsedLocation,
  transactionId: string,
  ts: number,
  hashKey: string | null
): { key: string; value: string } => {
  const key = hashKey
    ? CryptoJS.SHA256((hashKey + ts).toString()).toString(CryptoJS.enc.Base64)
    : '';
  const value = CryptoJS.enc.Base64.stringify(
    CryptoJS.HmacSHA256(url.pathname.replace('/apip', '') + url.search + ts + transactionId, key)
  );
  return { key: ws3, value };
};

const getReqHeaderInfo = (url: ParsedLocation) => {
  const transactionId = getTransactionId();
  const ts = Math.floor(Date.now() / 1000);
  const ws = getWS(url, transactionId, ts, getStorageKeyData(ws4));
  return {
    transactionId: { key: 'transaction-id', value: transactionId },
    ts: { key: 'timestamp', value: ts },
    ws,
  };
};

// --- 공개 API ---

export const getAuthInfo = (url: string) => {
  const targetUrl = getLocation(url);
  if (!targetUrl) throw new Error(`Invalid URL: ${url}`);
  const info = getReqHeaderInfo(targetUrl);

  const obfuscateValue1 = info.transactionId.value;
  const obfuscateValue2 = info.ws.value;
  const obfuscateValue3 = info.ts.value;

  const service = 'wehagomeet';
  const clientId = 'wehagomeet';
  const _token = getStorageKeyData('AUTH_A_TOKEN');
  const token = _token ? `Bearer ${_token}` : false;

  return { obfuscateValue1, obfuscateValue2, obfuscateValue3, service, clientId, token, info };
};

/** 인증 요청 헤더 (대부분의 API에 사용) */
export const getRequestHeadersChange = (url: string): Record<string, string | number | boolean> => {
  try {
    const { obfuscateValue1, obfuscateValue2, obfuscateValue3, service, clientId, token, info } = getAuthInfo(url);
    const result: Record<string, string | number | boolean> = {};
    result[info.transactionId.key] = obfuscateValue1;
    result[info.ws.key] = obfuscateValue2;
    result[info.ts.key] = obfuscateValue3;
    result['client-id'] = clientId;
    if (token) result['authorization'] = token;
    result['service'] = service;
    return result;
  } catch (err) {
    console.error('[signHeader]', err);
    return {};
  }
};

/** 메일 발송용 헤더 */
export const getSendEmailRequestHeaders = (url: string): Record<string, string | number | boolean> => {
  try {
    const { obfuscateValue1, obfuscateValue2, obfuscateValue3, clientId, token, info } = getAuthInfo(url);
    const result: Record<string, string | number | boolean> = {};
    result[info.transactionId.key] = obfuscateValue1;
    result[info.ws.key] = obfuscateValue2;
    result[info.ts.key] = obfuscateValue3;
    result['client-id'] = clientId;
    if (token) result['authorization'] = token;
    result['service'] = 'mail';
    result['Accept'] = '*/*';
    return result;
  } catch (err) {
    console.error('[signHeader/email]', err);
    return {};
  }
};

/** 문서 상세(ObjectStorage) API 헤더 — service: objectStorageService, method 헤더 포함 */
export const getDocumentDetailRequestHeaders = (url: string, method: string): Record<string, string | number | boolean> => {
  try {
    const { obfuscateValue1, obfuscateValue2, obfuscateValue3, clientId, token, info } = getAuthInfo(url);
    const result: Record<string, string | number | boolean> = {};
    result[info.transactionId.key] = obfuscateValue1;
    result[info.ws.key] = obfuscateValue2;
    result[info.ts.key] = obfuscateValue3;
    result['client-id'] = clientId;
    if (token) result['authorization'] = token;
    result['Accept'] = 'application/json';
    result['Content-Type'] = 'application/json';
    result['service'] = 'objectStorageService';
    result['method'] = method;
    return result;
  } catch (err) {
    console.error('[signHeader/documentDetail]', err);
    return {};
  }
};
